import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';
import Search from '@mui/icons-material/Search';
import Download from '@mui/icons-material/Download';

interface SpeakerPhrase {
  speaker: number;
  text: string;
  offsetInTicks: number;
  durationInTicks: number;
}

interface AzureTranscriptData {
  source?: string;
  timestamp?: string;
  duration?: string;
  combinedRecognizedPhrases?: Array<{
    channel: number;
    lexical: string;
    itn: string;
    maskedITN: string;
    display: string;
  }>;
  recognizedPhrases?: Array<{
    recognitionStatus: string;
    channel: number;
    speaker: number;
    offsetInTicks: number;
    durationInTicks: number;
    resultsBest?: Array<{
      confidence: number;
      lexical: string;
      itn: string;
      maskedITN: string;
      display: string;
    }>;
    nBest?: Array<{
      confidence: number;
      lexical: string;
      itn: string;
      maskedITN: string;
      display: string;
    }>;
  }>;
}

interface CallTranscriptProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  transcript: string | AzureTranscriptData | any;
  loading?: boolean;
  audioFileName?: string;
}

export const CallTranscript: React.FC<CallTranscriptProps> = ({
  transcript,
  loading = false,
  audioFileName,
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  // Parse transcript if it's a JSON string or object
  const parseTranscript = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: string | AzureTranscriptData | any
  ): string | AzureTranscriptData => {
    if (typeof data === 'string') {
      try {
        // Try to parse as JSON first
        const parsed = JSON.parse(data);
        if (parsed && (parsed.recognizedPhrases || parsed.combinedRecognizedPhrases)) {
          return parsed as AzureTranscriptData;
        }
      } catch {
        // If parsing fails, treat as plain text
      }
    } else if (typeof data === 'object' && data !== null) {
      // Handle Azure Speech Service format directly
      if (data.recognizedPhrases || data.combinedRecognizedPhrases) {
        return data as AzureTranscriptData;
      }

      // Check if it's wrapped in a response object
      if (data.rawContent) {
        try {
          const parsed = JSON.parse(data.rawContent);
          if (parsed && (parsed.recognizedPhrases || parsed.combinedRecognizedPhrases)) {
            return parsed as AzureTranscriptData;
          }
        } catch {
          return data.rawContent;
        }
      }

      // If it's a different object structure, try to convert to string for display
      return JSON.stringify(data, null, 2);
    }
    return data;
  };

  const parsedTranscript = parseTranscript(transcript);

  const HighlightedText: React.FC<{ text: string; search: string }> = ({ text, search }) => {
      if (!search.trim()) return <>{text}</>;

      const regex = new RegExp(`(${search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
      const parts = text.split(regex);

      return (
        <>
          {parts.map((part, i) =>
            regex.test(part) ? (
              <mark key={i} style={{ backgroundColor: '#fef08a' }}>
                {part}
              </mark>
            ) : (
              <span key={i}>{part}</span>
            )
          )}
        </>
      );
    }

  const parseAzureTranscript = (data: AzureTranscriptData): SpeakerPhrase[] => {
    if (!data.recognizedPhrases) return [];

    const phrases: SpeakerPhrase[] = [];

    for (const phrase of data.recognizedPhrases) {
      if (phrase.recognitionStatus === 'Success') {
        // Handle both nBest and resultsBest formats
        const bestResult = phrase.resultsBest?.[0] || phrase.nBest?.[0];
        if (bestResult && bestResult.display?.trim()) {
          phrases.push({
            speaker: phrase.speaker || 0,
            text: bestResult.display || bestResult.lexical || '',
            offsetInTicks: phrase.offsetInTicks || 0,
            durationInTicks: phrase.durationInTicks || 0,
          });
        }
      }
    }

    // Sort by time first
    const sortedPhrases = phrases.sort((a, b) => a.offsetInTicks - b.offsetInTicks);

    // Combine consecutive phrases from the same speaker
    const consolidatedPhrases: SpeakerPhrase[] = [];

    for (const phrase of sortedPhrases) {
      const lastPhrase = consolidatedPhrases[consolidatedPhrases.length - 1];

      if (lastPhrase && lastPhrase.speaker === phrase.speaker) {
        // Same speaker - combine the text
        lastPhrase.text += ' ' + phrase.text;
        // Update duration to include this phrase
        lastPhrase.durationInTicks =
          phrase.offsetInTicks + phrase.durationInTicks - lastPhrase.offsetInTicks;
      } else {
        // Different speaker or first phrase - add as new entry
        consolidatedPhrases.push({ ...phrase });
      }
    }

    return consolidatedPhrases;
  };

  const getSpeakerLabel = (speakerNumber: number): string => {
    // Start with Speaker 0 (use the speaker number as-is)
    return `Speaker ${speakerNumber}`;
  };

  const formatTranscript = (transcriptData: string | AzureTranscriptData) => {
    if (!transcriptData) return null;

    // Handle Azure Speech Service JSON format
    if (typeof transcriptData === 'object') {
      const phrases = parseAzureTranscript(transcriptData);

      if (phrases.length === 0) {
        // Fallback to combined recognized phrases if available
        if (transcriptData.combinedRecognizedPhrases?.[0]) {
          const combinedText =
            transcriptData.combinedRecognizedPhrases[0].display ||
            transcriptData.combinedRecognizedPhrases[0].lexical;
          return (
            <Box sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 2 }}>
              <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                <HighlightedText text={combinedText} search={searchTerm} />
              </Typography>
            </Box>
          );
        }
        return null;
      }

      return phrases.map((phrase, index) => {
        const speakerLabel = getSpeakerLabel(phrase.speaker);

        return (
          <Box key={index} mb={1.5}>
            <Typography
              variant="body2"
              sx={{
                lineHeight: 1.6,
                fontSize: '0.9rem',
              }}
            >
              <strong>{speakerLabel}:</strong>{' '}
              <HighlightedText text={phrase.text} search={searchTerm} />
            </Typography>
          </Box>
        );
      });
    }

    // Handle legacy string format
    const text = transcriptData as string;
    const lines = text.split(/\n|\r\n/).filter((line) => line.trim());

    return lines.map((line, index) => {
      // Check for diarized format with timestamp: [MM:SS] Speaker N: content
      const diarizedMatch = line.match(/^\[(\d{1,2}:\d{2})\]\s+(Speaker \d+):\s*(.*)/i);

      if (diarizedMatch) {
        const [, timestamp, speaker, content] = diarizedMatch;

        return (
          <Box key={index} mb={1.5}>
            <Typography
              variant="body2"
              sx={{
                lineHeight: 1.6,
                fontSize: '0.9rem',
              }}
            >
              <span style={{ color: '#666', fontSize: '0.8rem' }}>[{timestamp}]</span>{' '}
              <strong>{speaker}:</strong>{' '}
              <HighlightedText text={content} search={searchTerm} />
            </Typography>
          </Box>
        );
      }

      // Check if line starts with speaker indicator (fallback for other formats)
      const speakerMatch = line.match(/^(Speaker \d+|Dispatcher|Caller|Agent|Unit \d+):\s*(.*)/i);

      if (speakerMatch) {
        const [, speaker, content] = speakerMatch;

        return (
          <Box key={index} mb={1.5}>
            <Typography
              variant="body2"
              sx={{
                lineHeight: 1.6,
                fontSize: '0.9rem',
              }}
            >
              <strong>{speaker}:</strong>{' '}
              <HighlightedText text={content} search={searchTerm} />
            </Typography>
          </Box>
        );
      }

      // Check for timestamp patterns (standalone timestamps)
      const timestampMatch = line.match(/^(\d{2}:\d{2}:\d{2}|\d{1,2}:\d{2})\s+(.*)/);
      if (timestampMatch) {
        const [, timestamp, content] = timestampMatch;
        return (
          <Box key={index} mb={1.5}>
            <Typography
              variant="body2"
              sx={{
                lineHeight: 1.6,
                fontSize: '0.9rem',
              }}
            >
              <strong>{timestamp}:</strong>{' '}
              <HighlightedText text={content} search={searchTerm} />
            </Typography>
          </Box>
        );
      }

      // Regular line without speaker indicator
      return (
        <Typography
          key={index}
          variant="body2"
          sx={{
            mb: 1,
            lineHeight: 1.6,
            fontSize: '0.9rem',
          }}
        >
          <HighlightedText text={line} search={searchTerm} />
        </Typography>
      );
    });
  };

  const getTranscriptText = (data: string | AzureTranscriptData): string => {
    if (typeof data === 'string') {
      return data;
    }

    if (typeof data === 'object') {
      const phrases = parseAzureTranscript(data);
      if (phrases.length > 0) {
        return phrases
          .map((phrase) => `${getSpeakerLabel(phrase.speaker)}: ${phrase.text}`)
          .join('\n');
      }

      // Fallback to combined recognized phrases
      if (data.combinedRecognizedPhrases?.[0]) {
        return (
          data.combinedRecognizedPhrases[0].display ||
          data.combinedRecognizedPhrases[0].lexical ||
          ''
        );
      }
    }

    return '';
  };

  const downloadTranscript = () => {
    const transcriptText = getTranscriptText(parsedTranscript);
    if (!transcriptText) return;

    // Create filename from original audio filename
    const baseFileName = audioFileName
      ? audioFileName.replace(/\.[^/.]+$/, '') // Remove extension
      : 'transcript';
    const fileName = `${baseFileName}-transcript.doc`;

    // Create formatted content with header
    const currentDate = new Date().toLocaleDateString();
    const currentTime = new Date().toLocaleTimeString();

    // Create RTF content that can be opened by Word
    const rtfContent = `{\\rtf1\\ansi\\deff0 {\\fonttbl {\\f0 Times New Roman;}}
\\f0\\fs24 
\\b CALL TRANSCRIPT\\b0\\par
\\par
\\b Generated:\\b0 ${currentDate} ${currentTime}\\par
\\b Original File:\\b0 ${audioFileName || 'Unknown'}\\par
\\par
\\line
\\par
${transcriptText.replace(/\n/g, '\\par ')}
\\par
\\line
\\par
\\i End of Transcript\\i0
}`;

    // Create blob and download as RTF file (opens in Word)
    const blob = new Blob([rtfContent], { type: 'application/rtf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  return (
    <Paper sx={{ height: 'fit-content' }}>
      <Box p={3} borderBottom={1} borderColor="divider">
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Call Transcript</Typography>

          {parsedTranscript && (
            <Button
              onClick={downloadTranscript}
              startIcon={<Download />}
              size="small"
              variant="outlined"
            >
              Download
            </Button>
          )}
        </Box>

        {/* Search within transcript */}
        {parsedTranscript && (
          <TextField
            fullWidth
            size="small"
            placeholder="Search within transcript..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
        )}
      </Box>

      <Box p={3}>
        {loading ? (
          <Box textAlign="center" py={4}>
            <CircularProgress size={40} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Loading full transcription...
            </Typography>
          </Box>
        ) : parsedTranscript ? (
          <Box sx={{ maxHeight: 500, overflowY: 'auto' }}>{formatTranscript(parsedTranscript)}</Box>
        ) : (
          <Box textAlign="center" py={4}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Transcript not available yet
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Transcription may still be in progress
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
};
